import ReCAPTCHA from 'react-google-recaptcha';
import { createClient } from '@supabase/supabase-js';
import { useState, useEffect, useRef } from 'react';
import {
  LayoutDashboard, FileText, Settings,
  Plus, Trash2, Edit2, Save, X, Search,
  TrendingUp, Eye, Users,
  Check, AlertCircle, Palette,
  ExternalLink,
  ChevronLeft, ChevronRight,
  LogOut, Bell,
} from 'lucide-react';
import WebsitePreview from './WebsitePreview';
import PageEditor from './PageEditor';

// ============================================================
// CONFIG & CONSTANTS
// ============================================================
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

const SUPABASE_SERVICE_KEY = process.env.REACT_APP_SUPABASE_SERVICE_ROLE_KEY;

const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);
const supabaseAdmin = SUPABASE_SERVICE_KEY
  ? createClient(SUPABASE_URL, SUPABASE_SERVICE_KEY, { auth: { autoRefreshToken: false, persistSession: false } })
  : null;

const LOGO_URL = 'https://supa-site.aptana.co.id/storage/v1/object/public/Aptana-Website/home/logo%20aptana.svg';
const SITE_URL = 'https://aptana.co.id';
const ADMIN_EMAIL = 'brandmarketing@aptana.co.id';

// ============================================================
// STYLES  (injected once at module load)
// ============================================================
const _style = document.createElement('style');
_style.textContent = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=DM+Mono:wght@400;500&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  :root {
    --bg: #ffffff;
    --surface: #f8fafc;
    --surface2: #f1f5f9;
    --border: #e2e8f0;
    --accent: #1e3a8a;
    --accent2: #fbbf24;
    --danger: #dc2626;
    --warn: #f59e0b;
    --success: #16a34a;
    --text: #1e293b;
    --muted: #64748b;
    --font: 'Plus Jakarta Sans', sans-serif;
    --mono: 'DM Mono', monospace;
    --sb-bg: #ffffff;
    --sb-border: #e2e8f0;
    --sb-text: #475569;
    --sb-text-active: #1e3a8a;
    --sb-hover-bg: #1e3a8a;
    --sb-hover-text: #ffffff;
    --sb-active-bg: rgba(30,58,138,0.07);
    --sb-active-bar: #1e3a8a;
    --sb-section-color: #94a3b8;
    --sb-muted: #94a3b8;
    --sb-icon-opacity: 0.65;
    --sb-footer-border: #e2e8f0;
    --sb-logout-color: #dc2626;
  }
  body { background: var(--bg); color: var(--text); font-family: var(--font); min-height: 100vh; }
  ::-webkit-scrollbar { width: 5px; height: 5px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: rgba(30,58,138,0.15); border-radius: 3px; }

  .cms-layout { display: flex; height: 100vh; overflow: hidden; }
  .main { flex: 1; display: flex; flex-direction: column; overflow: hidden; min-width: 0; }
  .topbar { height: 56px; background: var(--surface); border-bottom: 1px solid var(--border); display: flex; align-items: center; justify-content: space-between; padding: 0 24px; flex-shrink: 0; }
  .topbar-title { font-size: 17px; font-weight: 700; }
  .content { flex: 1; overflow-y: auto; padding: 24px; }

  .cms-sidebar { width: 224px; background: var(--sb-bg); border-right: 1px solid var(--sb-border); display: flex; flex-direction: column; overflow: hidden; flex-shrink: 0; transition: width 0.25s cubic-bezier(0.4,0,0.2,1); position: relative; }
  .cms-sidebar.collapsed { width: 64px; }

  .sb-logo { display: flex; align-items: center; gap: 10px; padding: 16px 14px; border-bottom: 1px solid var(--sb-border); flex-shrink: 0; min-height: 72px; overflow: hidden; position: relative; }
  .sb-logo-inner { display: flex; align-items: center; gap: 10px; flex: 1; overflow: hidden; min-width: 0; }
  .sb-logo-inner img { height: 28px; width: auto; max-width: 100px; object-fit: contain; flex-shrink: 0; }
  .sb-logo-text { flex: 1; overflow: hidden; white-space: nowrap; min-width: 0; }
  .sb-logo-text .t1 { font-size: 11px; font-weight: 700; color: var(--text); letter-spacing: 0.2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .sb-logo-text .t2 { font-size: 9px; color: var(--sb-muted); font-family: var(--mono); margin-top: 2px; }
  .sb-logo-collapsed { display: flex; align-items: center; justify-content: center; width: 100%; }
  .sb-toggle { width: 26px; height: 26px; border-radius: 6px; background: var(--surface2); border: 1px solid var(--sb-border); cursor: pointer; color: var(--muted); display: flex; align-items: center; justify-content: center; flex-shrink: 0; padding: 0; transition: background 0.15s, color 0.15s, border-color 0.15s; }
  .sb-toggle:hover { background: var(--sb-hover-bg); color: white; border-color: var(--sb-hover-bg); }

  .sb-section { font-size: 9px; font-weight: 700; letter-spacing: 1.3px; text-transform: uppercase; color: var(--sb-section-color); padding: 14px 14px 5px; white-space: nowrap; overflow: hidden; }
  .cms-sidebar.collapsed .sb-section { padding: 10px 0 4px; text-align: center; font-size: 6px; letter-spacing: 0; }

  .sb-nav { flex: 1; padding: 6px 8px; overflow-y: auto; overflow-x: hidden; }
  .sb-nav::-webkit-scrollbar { width: 3px; }
  .sb-nav::-webkit-scrollbar-thumb { background: rgba(30,58,138,0.12); }

  .sb-item { display: flex; align-items: center; gap: 10px; padding: 9px 10px; border-radius: 8px; cursor: pointer; font-size: 13px; font-weight: 500; color: var(--sb-text); transition: background 0.13s, color 0.13s; margin-bottom: 1px; border: none; background: none; width: 100%; text-align: left; font-family: var(--font); white-space: nowrap; overflow: hidden; position: relative; }
  .sb-item:hover { background: var(--sb-hover-bg); color: var(--sb-hover-text); }
  .sb-item:hover .sb-icon { opacity: 1; }
  .sb-item.active { background: var(--sb-active-bg); color: var(--sb-text-active); font-weight: 600; }
  .sb-item.active::before { content: ''; position: absolute; left: 0; top: 6px; bottom: 6px; width: 3px; background: var(--sb-active-bar); border-radius: 0 2px 2px 0; }
  .sb-item.active .sb-icon { opacity: 1; }

  .sb-icon { flex-shrink: 0; width: 18px; height: 18px; display: flex; align-items: center; justify-content: center; opacity: var(--sb-icon-opacity); transition: opacity 0.13s; }
  .sb-label { flex: 1; overflow: hidden; text-overflow: ellipsis; transition: opacity 0.15s, width 0.15s; }
  .cms-sidebar.collapsed .sb-label { opacity: 0; width: 0; flex: 0; overflow: hidden; }

  .sb-item .sb-tip { display: none; position: fixed; left: 68px; background: var(--accent); color: white; font-size: 12px; font-weight: 600; padding: 5px 11px; border-radius: 7px; white-space: nowrap; z-index: 9999; pointer-events: none; box-shadow: 0 4px 16px rgba(30,58,138,0.25); border: 1px solid rgba(30,58,138,0.2); transform: translateY(-50%); }
  .sb-item .sb-tip::before { content: ''; position: absolute; right: 100%; top: 50%; transform: translateY(-50%); border: 5px solid transparent; border-right-color: var(--accent); }
  .cms-sidebar.collapsed .sb-item:hover .sb-tip { display: block; }

  .sb-item.logout-item { color: var(--sb-logout-color) !important; }
  .sb-item.logout-item .sb-icon { color: var(--sb-logout-color) !important; opacity: 1 !important; }
  .sb-item.logout-item:hover { background: rgba(220,38,38,0.07) !important; color: var(--sb-logout-color) !important; }

  .sb-footer { padding: 8px 8px 14px; border-top: 1px solid var(--sb-footer-border); flex-shrink: 0; overflow: hidden; }
  .sb-email { font-size: 10px; color: var(--sb-muted); padding: 2px 10px 7px; font-family: var(--mono); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .cms-sidebar.collapsed .sb-email { display: none; }

  .stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; margin-bottom: 20px; }
  .stat-card { background: var(--surface); border: 1px solid var(--border); border-radius: 10px; padding: 18px; }
  .stat-label { font-size: 11px; color: var(--muted); font-weight: 500; margin-bottom: 8px; display: flex; align-items: center; gap: 5px; }
  .stat-value { font-size: 26px; font-weight: 800; letter-spacing: -1px; }
  .stat-change { font-size: 11px; color: var(--success); margin-top: 3px; }

  .chart-card { background: var(--surface); border: 1px solid var(--border); border-radius: 10px; padding: 18px; margin-bottom: 20px; }
  .chart-title { font-size: 14px; font-weight: 600; margin-bottom: 14px; }

  .card { background: var(--surface); border: 1px solid var(--border); border-radius: 10px; padding: 18px; }
  .card-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 14px; }
  .card-title { font-size: 14px; font-weight: 600; }

  .btn { display: inline-flex; align-items: center; gap: 5px; padding: 7px 13px; border-radius: 7px; font-size: 13px; font-weight: 600; cursor: pointer; border: none; transition: all 0.15s; font-family: var(--font); white-space: nowrap; }
  .btn-primary { background: var(--accent); color: #fff; }
  .btn-primary:hover { background: #172554; }
  .btn-ghost { background: var(--surface2); color: var(--text); border: 1px solid var(--border); }
  .btn-ghost:hover { border-color: var(--accent); color: var(--accent); }
  .btn-danger { background: rgba(220,38,38,0.07); color: var(--danger); border: 1px solid rgba(220,38,38,0.15); }
  .btn-danger:hover { background: rgba(220,38,38,0.12); }
  .btn-success { background: var(--success); color: #fff; }
  .btn-yellow { background: var(--accent2); color: #1e293b; }
  .btn-yellow:hover { background: #f59e0b; }
  .btn-sm { padding: 4px 9px; font-size: 12px; }

  .table { width: 100%; border-collapse: collapse; }
  .table th { text-align: left; font-size: 11px; font-weight: 600; color: var(--muted); text-transform: uppercase; letter-spacing: 0.8px; padding: 9px 13px; border-bottom: 1px solid var(--border); }
  .table td { padding: 12px 13px; border-bottom: 1px solid var(--border); font-size: 13px; }
  .table tr:last-child td { border-bottom: none; }
  .table tr:hover td { background: rgba(30,58,138,0.02); }
  .table-actions { display: flex; gap: 5px; }

  .badge { display: inline-flex; align-items: center; gap: 3px; padding: 2px 7px; border-radius: 20px; font-size: 11px; font-weight: 600; }
  .badge-green { background: rgba(22,163,74,0.1); color: var(--success); }
  .badge-blue { background: rgba(30,58,138,0.1); color: var(--accent); }
  .badge-gray { background: var(--surface2); color: var(--muted); }
  .badge-warn { background: rgba(245,158,11,0.1); color: var(--warn); }

  .form-group { margin-bottom: 14px; }
  .form-label { display: block; font-size: 11px; font-weight: 600; color: var(--muted); margin-bottom: 5px; text-transform: uppercase; letter-spacing: 0.6px; }
  .form-input { width: 100%; padding: 9px 11px; border-radius: 7px; background: var(--bg); border: 1px solid var(--border); color: var(--text); font-family: var(--font); font-size: 13px; outline: none; transition: border 0.15s; }
  .form-input:focus { border-color: var(--accent); }
  .form-textarea { resize: vertical; min-height: 100px; }
  .form-select { cursor: pointer; }

  .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.45); display: flex; align-items: center; justify-content: center; z-index: 1000; padding: 20px; backdrop-filter: blur(2px); }
  .modal { background: var(--bg); border: 1px solid var(--border); border-radius: 14px; width: 100%; max-width: 560px; max-height: 85vh; overflow-y: auto; box-shadow: 0 24px 60px rgba(0,0,0,0.15); }
  .modal-header { display: flex; align-items: center; justify-content: space-between; padding: 18px 22px; border-bottom: 1px solid var(--border); position: sticky; top: 0; background: var(--bg); border-radius: 14px 14px 0 0; }
  .modal-title { font-size: 15px; font-weight: 700; }
  .modal-body { padding: 22px; }
  .modal-footer { display: flex; justify-content: flex-end; gap: 8px; padding: 14px 22px; border-top: 1px solid var(--border); }

  .search-wrap { position: relative; }
  .search-wrap svg { position: absolute; left: 9px; top: 50%; transform: translateY(-50%); color: var(--muted); }
  .search-input { padding: 7px 11px 7px 30px; border-radius: 7px; background: var(--surface2); border: 1px solid var(--border); color: var(--text); font-family: var(--font); font-size: 13px; outline: none; width: 200px; transition: border 0.15s; }
  .search-input:focus { border-color: var(--accent); }

  .toast { position: fixed; bottom: 20px; right: 20px; background: var(--bg); border: 1px solid var(--border); border-radius: 9px; padding: 11px 16px; display: flex; align-items: center; gap: 9px; font-size: 13px; font-weight: 500; z-index: 9999; animation: slideUp 0.25s ease; box-shadow: 0 4px 20px rgba(0,0,0,0.1); }
  .toast.success { border-color: var(--success); }
  .toast.error   { border-color: var(--danger); }
  @keyframes slideUp { from { transform: translateY(16px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }

  .login-page { min-height: 100vh; display: flex; align-items: center; justify-content: center; background: var(--bg); background-image: radial-gradient(ellipse at 20% 50%, rgba(30,58,138,0.05) 0%, transparent 60%); }
  .login-card { background: var(--surface); border: 1px solid var(--border); border-radius: 18px; padding: 36px; width: 100%; max-width: 380px; box-shadow: 0 8px 40px rgba(0,0,0,0.07); }
  .login-title { font-size: 20px; font-weight: 800; margin-bottom: 4px; margin-top: 18px; }
  .login-sub   { font-size: 13px; color: var(--muted); margin-bottom: 24px; }

  .empty { text-align: center; padding: 48px 24px; color: var(--muted); }
  .empty-icon { font-size: 36px; margin-bottom: 10px; }
  .empty-text { font-size: 13px; }

  .color-preview { width: 30px; height: 30px; border-radius: 5px; border: 1px solid var(--border); flex-shrink: 0; }

  .pages-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px; }
  .page-card { background: var(--bg); border: 1.5px solid var(--border); border-radius: 10px; overflow: hidden; transition: all 0.2s; }
  .page-card:hover { border-color: var(--accent); box-shadow: 0 4px 16px rgba(30,58,138,0.1); transform: translateY(-1px); }
  .page-card-thumb { height: 110px; background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%); display: flex; align-items: center; justify-content: center; font-size: 28px; cursor: pointer; position: relative; overflow: hidden; }
  .page-card-thumb:hover .thumb-overlay { opacity: 1; }
  .thumb-overlay { position: absolute; inset: 0; background: rgba(30,58,138,0.7); display: flex; align-items: center; justify-content: center; opacity: 0; transition: opacity 0.2s; color: white; font-size: 13px; font-weight: 600; gap: 6px; }
  .page-card-body { padding: 12px 14px; }
  .page-card-name { font-size: 13px; font-weight: 700; margin-bottom: 3px; }
  .page-card-slug { font-size: 11px; color: var(--muted); font-family: var(--mono); margin-bottom: 10px; }
  .page-card-footer { display: flex; align-items: center; justify-content: space-between; }

  /* ── Notification Bell ─────────────────────────────────────── */
  .notif-wrap { position: relative; }
  .notif-btn  { position: relative; width: 34px; height: 34px; border-radius: 8px; background: none; border: 1px solid var(--border); color: var(--muted); display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.15s; }
  .notif-btn:hover { background: var(--surface2); color: var(--accent); border-color: var(--accent); }
  .notif-badge { position: absolute; top: -5px; right: -5px; min-width: 17px; height: 17px; background: #dc2626; color: white; font-size: 10px; font-weight: 700; border-radius: 20px; display: flex; align-items: center; justify-content: center; padding: 0 4px; border: 2px solid var(--bg); pointer-events: none; }
  .notif-panel { position: absolute; top: calc(100% + 8px); right: 0; width: 340px; background: var(--bg); border: 1px solid var(--border); border-radius: 12px; box-shadow: 0 8px 32px rgba(0,0,0,0.12); z-index: 500; overflow: hidden; }
  .notif-head  { display: flex; align-items: center; justify-content: space-between; padding: 12px 16px; border-bottom: 1px solid var(--border); }
  .notif-head-title { font-size: 13px; font-weight: 700; }
  .notif-mark-read { font-size: 11px; color: var(--accent); cursor: pointer; background: none; border: none; font-family: var(--font); font-weight: 600; padding: 0; }
  .notif-mark-read:hover { text-decoration: underline; }
  .notif-list { max-height: 360px; overflow-y: auto; }
  .notif-item { display: flex; gap: 10px; padding: 11px 16px; border-bottom: 1px solid var(--border); transition: background 0.13s; cursor: default; }
  .notif-item:last-child { border-bottom: none; }
  .notif-item.unread { background: rgba(30,58,138,0.04); }
  .notif-item:hover { background: var(--surface2); }
  .notif-dot { width: 7px; height: 7px; border-radius: 50%; background: var(--accent); flex-shrink: 0; margin-top: 5px; }
  .notif-dot.read { background: transparent; }
  .notif-body { flex: 1; min-width: 0; }
  .notif-title { font-size: 12px; font-weight: 600; color: var(--text); margin-bottom: 2px; }
  .notif-sub   { font-size: 11px; color: var(--muted); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .notif-time  { font-size: 10px; color: var(--muted); margin-top: 3px; font-family: var(--mono); }
  .notif-empty { text-align: center; padding: 32px 16px; color: var(--muted); font-size: 13px; }
`;
document.head.appendChild(_style);

// ============================================================
// DEFAULT HTML TEMPLATE
// ============================================================
const getDefaultHTML = (pageName) => `<!DOCTYPE html>
<html lang="id">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${pageName} - Aptana</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: 'Segoe UI', sans-serif; color: #1e293b; }
    header { background: #1e3a8a; color: white; padding: 18px 40px; display: flex; align-items: center; justify-content: space-between; }
    header .logo { height: 36px; object-fit: contain; filter: brightness(0) invert(1); }
    nav a { color: rgba(255,255,255,0.75); text-decoration: none; margin-left: 24px; font-size: 14px; transition: color 0.2s; }
    nav a:hover { color: white; }
    .hero { padding: 80px 40px; text-align: center; background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%); }
    .hero h1 { font-size: 52px; font-weight: 800; color: #1e3a8a; margin-bottom: 16px; letter-spacing: -1.5px; line-height: 1.1; }
    .hero p  { font-size: 18px; color: #64748b; max-width: 560px; margin: 0 auto 32px; line-height: 1.7; }
    .btn { display: inline-block; padding: 14px 32px; background: #1e3a8a; color: white; border-radius: 8px; text-decoration: none; font-weight: 700; font-size: 15px; transition: all 0.2s; }
    .btn:hover { background: #172554; transform: translateY(-2px); }
    .btn-outline { background: transparent; color: #1e3a8a; border: 2px solid #1e3a8a; margin-left: 12px; }
    .section { padding: 72px 40px; max-width: 1200px; margin: 0 auto; }
    .section-title { font-size: 36px; font-weight: 800; color: #1e293b; margin-bottom: 12px; }
    .section-sub   { font-size: 16px; color: #64748b; margin-bottom: 40px; }
    .grid-3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; }
    .feature-card  { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 28px; }
    .feature-icon  { font-size: 32px; margin-bottom: 14px; }
    .feature-title { font-size: 17px; font-weight: 700; margin-bottom: 8px; color: #1e293b; }
    .feature-desc  { font-size: 14px; color: #64748b; line-height: 1.7; }
    footer { background: #0f172a; color: rgba(255,255,255,0.5); text-align: center; padding: 28px; font-size: 13px; }
  </style>
</head>
<body>
  <header>
    <img src="${LOGO_URL}" alt="Aptana" class="logo" />
    <nav>
      <a href="/">Home</a><a href="/multichannel">Multichannel</a><a href="/kuponku">Kuponku</a><a href="/tentang">Tentang</a><a href="/blog">Blog</a><a href="/karir">Karir</a><a href="/kontak">Kontak</a>
    </nav>
  </header>
  <div class="hero">
    <h1>${pageName}</h1>
    <p>Selamat datang di halaman ${pageName}. Edit konten halaman ini sesuai kebutuhan website Aptana.</p>
    <a href="/kontak" class="btn">Hubungi Kami</a>
    <a href="/layanan" class="btn btn-outline">Lihat Layanan</a>
  </div>
  <div class="section">
    <div class="section-title">Layanan Unggulan</div>
    <div class="section-sub">Solusi terbaik untuk kebutuhan bisnis kamu</div>
    <div class="grid-3">
      <div class="feature-card"><div class="feature-icon">🚀</div><div class="feature-title">Fitur Pertama</div><div class="feature-desc">Deskripsi fitur pertama.</div></div>
      <div class="feature-card"><div class="feature-icon">💡</div><div class="feature-title">Fitur Kedua</div><div class="feature-desc">Deskripsi fitur kedua.</div></div>
      <div class="feature-card"><div class="feature-icon">⚡</div><div class="feature-title">Fitur Ketiga</div><div class="feature-desc">Deskripsi fitur ketiga.</div></div>
    </div>
  </div>
  <footer><p>© ${new Date().getFullYear()} Aptana Citra Solusindo. All rights reserved.</p></footer>
</body>
</html>`;

// ============================================================
// TOAST
// ============================================================
function Toast({ message, type, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [onClose]);
  return (
    <div className={`toast ${type}`}>
      {type === 'success'
        ? <Check size={14} color="#16a34a" />
        : <AlertCircle size={14} color="#dc2626" />}
      {message}
    </div>
  );
}

// ============================================================
// SIDEBAR
// ============================================================
function CMSSidebar({ navItems, page, setPage, session, collapsed, setCollapsed }) {
  return (
    <div className={`cms-sidebar${collapsed ? ' collapsed' : ''}`}>
      <div className="sb-logo">
        {collapsed ? (
          <div className="sb-logo-collapsed">
            <button className="sb-toggle" onClick={() => setCollapsed(false)} title="Expand sidebar">
              <ChevronRight size={13} />
            </button>
          </div>
        ) : (
          <div className="sb-logo-inner">
            <img src={LOGO_URL} alt="Aptana" />
            <div className="sb-logo-text">
              <div className="t1">Marketing CMS</div>
              <div className="t2">aptana.co.id</div>
            </div>
            <button className="sb-toggle" onClick={() => setCollapsed(true)} title="Collapse sidebar">
              <ChevronLeft size={13} />
            </button>
          </div>
        )}
      </div>

      <div className="sb-nav">
        <div className="sb-section">{collapsed ? '•••' : 'Menu Utama'}</div>
        {navItems.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            className={`sb-item${page === id ? ' active' : ''}`}
            onClick={() => setPage(id)}
          >
            <span className="sb-icon"><Icon size={16} /></span>
            <span className="sb-label">{label}</span>
            <span className="sb-tip">{label}</span>
          </button>
        ))}
      </div>

      <div className="sb-footer">
        <div className="sb-email">{session?.user?.email}</div>
        <button
          className="sb-item logout-item"
          onClick={() => supabase.auth.signOut()}
          title="Keluar"
        >
          <span className="sb-icon"><LogOut size={16} /></span>
          <span className="sb-label">Keluar</span>
          <span className="sb-tip">Keluar</span>
        </button>
      </div>
    </div>
  );
}

// ============================================================
// LOGIN
// ============================================================
function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const recaptchaRef = useRef(null);

  const handleLogin = async () => {
    if (!recaptchaToken) { setError("Silakan centang 'I'm not a robot' terlebih dahulu."); return; }
    setLoading(true); setError('');
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      setError('Email atau password salah.');
      setRecaptchaToken(null);
      recaptchaRef.current?.reset();
    }
    setLoading(false);
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <img src={LOGO_URL} alt="Aptana" style={{ height: 34, objectFit: 'contain' }} />
        <div className="login-title">Selamat datang 👋</div>
        <div className="login-sub">Masuk ke Aptana Marketing CMS</div>
        {error && <div style={{ color: 'var(--danger)', fontSize: 13, marginBottom: 12 }}>{error}</div>}
        <div className="form-group">
          <label className="form-label">Email</label>
          <input className="form-input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="email@aptana.co.id" />
        </div>
        <div className="form-group">
          <label className="form-label">Password</label>
          <input className="form-input" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" onKeyDown={e => e.key === 'Enter' && handleLogin()} />
        </div>
        <div style={{ margin: '16px 0', display: 'flex', justifyContent: 'center' }}>
          <ReCAPTCHA ref={recaptchaRef} sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || "6LeMuaAsAAAAAKvzCJPk3Gde3PTbY7La9kWKCNB7"} onChange={token => setRecaptchaToken(token)} onExpired={() => setRecaptchaToken(null)} />
        </div>
        <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', marginTop: 6 }} onClick={handleLogin} disabled={loading || !recaptchaToken}>
          {loading ? 'Masuk...' : 'Masuk ke CMS'}
        </button>
      </div>
    </div>
  );
}

// ============================================================
// WEBSITE DESIGNER
// ============================================================
function WebsiteDesigner({ toast }) {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPage, setEditingPage] = useState(null);
  const [htmlContent, setHtmlContent] = useState('');
  const [savedHtml, setSavedHtml] = useState('');
  const [modal, setModal] = useState(false);
  const [newPage, setNewPage] = useState({ name: '', slug: '', meta_title: '', meta_description: '' });

  const fetchPages = async () => {
    setLoading(true);
    const { data } = await supabase.from('pages').select('*').order('created_at');
    setPages(data || []);
    setLoading(false);
  };
  useEffect(() => { fetchPages(); }, []);

  const openEditor = (page) => {
    const html = page.html || getDefaultHTML(page.name);
    setEditingPage(page);
    setHtmlContent(html);
    setSavedHtml(html);
  };

  const saveGrapesJSPage = async (html) => {
    if (!editingPage) return;
    // TODO: Verify RLS policy is enabled on this table
    const { error } = await supabase.from('pages').update({ html, updated_at: new Date().toISOString() }).eq('id', editingPage.id);
    if (error) { toast('Gagal menyimpan: ' + error.message, 'error'); throw error; }
    toast('Tersimpan! ✅', 'success');
    setSavedHtml(html);
    setHtmlContent(html);
    setEditingPage(prev => ({ ...prev, html }));
    fetchPages();
  };

  const savePage = async () => {
    if (!editingPage) return;
    // TODO: Verify RLS policy is enabled on this table
    const { error } = await supabase.from('pages').update({ html: htmlContent, updated_at: new Date().toISOString() }).eq('id', editingPage.id);
    if (error) toast(`Gagal menyimpan: ${error.message}`, 'error');
    else { toast('Tersimpan! ✅', 'success'); setSavedHtml(htmlContent); setEditingPage(prev => ({ ...prev, html: htmlContent })); fetchPages(); }
  };

  const publishPage = async () => {
    if (!editingPage) return;
    if (htmlContent !== savedHtml) await savePage();
    // TODO: Verify RLS policy is enabled on this table
    const { error } = await supabase.from('pages').update({ status: 'published' }).eq('id', editingPage.id);
    if (!error) { toast(`🟢 ${editingPage.name} dipublikasikan!`, 'success'); setEditingPage(prev => ({ ...prev, status: 'published' })); fetchPages(); }
  };

  const unpublishPage = async () => {
    if (!editingPage) return;
    // TODO: Verify RLS policy is enabled on this table
    const { error } = await supabase.from('pages').update({ status: 'draft' }).eq('id', editingPage.id);
    if (!error) { toast(`${editingPage.name} dijadikan draft`, 'success'); setEditingPage(prev => ({ ...prev, status: 'draft' })); fetchPages(); }
  };

  const addPage = async () => {
    if (!newPage.name || !newPage.slug) return toast('Nama dan slug harus diisi', 'error');
    // TODO: Verify RLS policy is enabled on this table
    const { error } = await supabase.from('pages').insert([{
      name: newPage.name, slug: newPage.slug,
      meta_title: newPage.meta_title, meta_description: newPage.meta_description,
      html: getDefaultHTML(newPage.name), status: 'draft',
    }]);
    if (error) toast('Gagal menambah halaman', 'error');
    else { toast('Halaman baru ditambahkan!', 'success'); setModal(false); setNewPage({ name: '', slug: '', meta_title: '', meta_description: '' }); fetchPages(); }
  };

  const deletePage = async (id) => {
    if (!window.confirm('Hapus halaman ini?')) return;
    // TODO: Verify RLS policy is enabled on this table
    await supabase.from('pages').delete().eq('id', id);
    toast('Halaman dihapus', 'success');
    fetchPages();
  };

  const getPageIcon = (slug) => ({ home: '🏠', multichannel: '📡', kuponku: '🎫', tentang: '👥', blog: '📝', karir: '💼', kontak: '📞', layanan: '⚡', portfolio: '🎨', 'submission-form': '📨' }[slug] || '📄');
  const getLiveUrl = (slug) => slug === 'home' ? SITE_URL : `${SITE_URL}/${slug}`;

  if (editingPage) {
    return (
      // key={editingPage.id} forces a full remount whenever the page changes.
      <PageEditor
        key={editingPage.id}
        initialHTML={htmlContent}
        onSave={saveGrapesJSPage}
        onClose={() => setEditingPage(null)}
        pageName={editingPage.name}
        pages={pages}
        onPageChange={openEditor}
        editingPage={editingPage}
        publishPage={publishPage}
        unpublishPage={unpublishPage}
        getLiveUrl={getLiveUrl}
      />
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 19, fontWeight: 700 }}>Website Design</div>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 2 }}>Kelola & edit semua halaman website Aptana</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <a href={SITE_URL} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
            <button className="btn btn-ghost btn-sm"><ExternalLink size={13} /> Buka Website</button>
          </a>
          <button className="btn btn-primary" onClick={() => setModal(true)}><Plus size={13} /> Halaman Baru</button>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--muted)' }}>Memuat halaman...</div>
      ) : (
        <div className="pages-grid">
          <div className="page-card">
            <div className="page-card-thumb">
              <span>{getPageIcon('submission-form')}</span>
              <div className="thumb-overlay">Halaman Sistem</div>
            </div>
            <div className="page-card-body">
              <div className="page-card-name">Submission Form</div>
              <div className="page-card-slug">/submission-form</div>
              <div className="page-card-footer">
                <span className="badge badge-green">🟢 Live · Sistem</span>
                <a href={getLiveUrl('submission-form')} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                  <button className="btn btn-ghost btn-sm"><ExternalLink size={11} /> Buka</button>
                </a>
              </div>
            </div>
          </div>
          {pages.map(page => (
            <div key={page.id} className="page-card">
              <div className="page-card-thumb" onClick={() => openEditor(page)}>
                <span>{getPageIcon(page.slug)}</span>
                <div className="thumb-overlay"><Edit2 size={14} /> Edit Halaman</div>
              </div>
              <div className="page-card-body">
                <div className="page-card-name">{page.name}</div>
                <div className="page-card-slug">/{page.slug}</div>
                <div className="page-card-footer">
                  <span className={`badge ${page.status === 'published' ? 'badge-green' : 'badge-warn'}`}>
                    {page.status === 'published' ? '🟢 Live' : '⚪ Draft'}
                  </span>
                  <div style={{ display: 'flex', gap: 5 }}>
                    <a href={getLiveUrl(page.slug)} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                      <button className="btn btn-ghost btn-sm"><ExternalLink size={11} /></button>
                    </a>
                    <button className="btn btn-ghost btn-sm" onClick={() => openEditor(page)}><Edit2 size={11} /> Edit</button>
                    <button className="btn btn-danger btn-sm" onClick={() => deletePage(page.id)}><Trash2 size={11} /></button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <div className="modal-title">Tambah Halaman Baru</div>
              <button className="btn btn-ghost btn-sm" onClick={() => setModal(false)}><X size={13} /></button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Nama Halaman</label>
                <input className="form-input" value={newPage.name} onChange={e => setNewPage({ ...newPage, name: e.target.value, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })} placeholder="contoh: Tentang Kami" />
              </div>
              <div className="form-group">
                <label className="form-label">Slug URL</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 12, color: 'var(--muted)', whiteSpace: 'nowrap' }}>aptana.co.id/</span>
                  <input className="form-input" value={newPage.slug} onChange={e => setNewPage({ ...newPage, slug: e.target.value })} placeholder="tentang-kami" />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Meta Title</label>
                <input className="form-input" value={newPage.meta_title} onChange={e => setNewPage({ ...newPage, meta_title: e.target.value })} placeholder="Judul untuk Google..." />
              </div>
              <div className="form-group">
                <label className="form-label">Meta Description</label>
                <textarea className="form-input form-textarea" style={{ minHeight: 70 }} value={newPage.meta_description} onChange={e => setNewPage({ ...newPage, meta_description: e.target.value })} placeholder="Deskripsi untuk Google..." />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setModal(false)}>Batal</button>
              <button className="btn btn-primary" onClick={addPage}><Plus size={13} /> Tambah</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// LEADS
// ============================================================
const LEADS_SHEETS = [
  {
    id: 'aptana',
    label: 'Aptana Leads',
    icon: '📋',
    url: 'https://docs.google.com/spreadsheets/d/1co5aVru3Yi7bJ34O0LP6dkbPFQzy4LEUlpMsCJ1unOk/edit?gid=1317027591&rm=minimal',
  },
  {
    id: 'kuponku',
    label: 'Kuponku Leads',
    icon: '🎫',
    url: 'https://docs.google.com/spreadsheets/d/18FY-L3E7bD-ZpV6xciVKEAvdMTOlyvYX0ORZuIadGTA/edit?gid=1826597003&rm=minimal',
  },
  {
    id: 'salesql-kuponku',
    label: 'SalesQL Kuponku',
    icon: '🔍',
    url: 'https://docs.google.com/spreadsheets/d/178ZqGfDdZPKqRjnxLBYLZT8hpF_DO4Zy4djXp_57pY8/edit?rm=minimal',
  },
  {
    id: 'salesql-multichannel',
    label: 'SalesQL Multichannel',
    icon: '📡',
    url: 'https://docs.google.com/spreadsheets/d/1DeJ8TFWzJGY4Nun6d3wMkh9vFOun1xNzhSbQcpXMW68/edit?gid=0&rm=minimal',
  },
];

function LeadsPage() {
  const [active, setActive] = useState('aptana');
  const sheet = LEADS_SHEETS.find(s => s.id === active);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 56px - 48px)', gap: 0 }}>

      {/* Header + tab switcher */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexShrink: 0 }}>
        <div>
          <div style={{ fontSize: 19, fontWeight: 700 }}>Leads</div>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 2 }}>Data leads langsung dari Google Sheets — login Google diperlukan</div>
        </div>
        <div style={{ display: 'flex', gap: 6, background: 'var(--surface2)', padding: '4px', borderRadius: 9, border: '1px solid var(--border)' }}>
          {LEADS_SHEETS.map(s => (
            <button
              key={s.id}
              onClick={() => setActive(s.id)}
              className={`btn btn-sm${active === s.id ? ' btn-primary' : ' btn-ghost'}`}
              style={{ borderRadius: 6 }}
            >
              {s.icon} {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* Sheet iframe — fills remaining height */}
      <div style={{ flex: 1, borderRadius: 10, overflow: 'hidden', border: '1px solid var(--border)', background: 'var(--surface)', minHeight: 0 }}>
        <iframe
          key={sheet.id}
          src={sheet.url}
          title={sheet.label}
          width="100%"
          height="100%"
          style={{ border: 'none', display: 'block' }}
          allow="clipboard-read; clipboard-write"
        />
      </div>

    </div>
  );
}

// ============================================================
// TRAFFIC
// ============================================================
function TrafficPage() {
  return (
    <div>
      <div style={{ background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)', borderRadius: 10, padding: '14px 18px', marginBottom: 20, color: 'white', display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ fontSize: 22 }}>✅</div>
        <div>
          <div style={{ fontWeight: 600, fontSize: 13 }}>Google Analytics 4 Terhubung</div>
          <div style={{ fontSize: 11, opacity: 0.9 }}>Measurement ID: G-8CGCSG5T7T</div>
        </div>
      </div>
      <div className="chart-card">
        <div className="chart-title">📊 Google Analytics 4 Dashboard</div>
        <iframe
          width="100%"
          height="950"
          src="https://lookerstudio.google.com/embed/reporting/36387e69-fb76-4418-a9d0-f95e91353e97/page/4VDGB"
          frameBorder="0"
          style={{ border: 0, borderRadius: 8 }}
          allowFullScreen
          title="GA4 Dashboard"
        />
      </div>
    </div>
  );
}

// ============================================================
// ARTICLES
// ============================================================
function Articles({ toast }) {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', content: '', status: 'draft', category: '', meta_title: '', meta_description: '', focus_keyword: '', slug: '', featured_image: '', is_popup: false, popup_pages: [] });

  const POPUP_PAGES = [
    { slug: 'home', label: '🏠 Home' },
    { slug: 'multichannel', label: '📡 Multichannel' },
    { slug: 'kuponku', label: '🎫 Kuponku' },
    { slug: 'tentang', label: '👥 Tentang Kami' },
    { slug: 'blog', label: '📝 Blog' },
    { slug: 'karir', label: '💼 Karir' },
    { slug: 'kontak', label: '📞 Kontak' },
  ];
  const togglePopupPage = (slug) => setForm(prev => ({
    ...prev,
    popup_pages: prev.popup_pages.includes(slug)
      ? prev.popup_pages.filter(p => p !== slug)
      : [...prev.popup_pages, slug],
  }));
  const [aiLoading, setAiLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  const fetchData = async () => { setLoading(true); const { data } = await supabase.from('articles').select('*').order('created_at', { ascending: false }); setArticles(data || []); setLoading(false); };
  useEffect(() => { fetchData(); }, []);

  const EMPTY_FORM = { title: '', content: '', status: 'draft', category: '', meta_title: '', meta_description: '', focus_keyword: '', slug: '', featured_image: '', is_popup: false, popup_pages: [] };
  const openNew = () => { setEditing(null); setForm(EMPTY_FORM); setModal(true); };
  const openNewPopup = () => { setEditing(null); setForm({ ...EMPTY_FORM, is_popup: true, category: 'Newsletter' }); setModal(true); };
  const openEdit = (a) => { setEditing(a); setForm({ title: a.title, content: a.content || '', status: a.status, category: a.category || '', meta_title: a.meta_title || '', meta_description: a.meta_description || '', focus_keyword: a.focus_keyword || '', slug: a.slug || '', featured_image: a.featured_image || '', is_popup: a.is_popup || false, popup_pages: a.popup_pages || [] }); setModal(true); };

  const save = async () => {
    if (!form.title) return toast('Judul tidak boleh kosong', 'error');
    if (!form.slug) return toast('Slug URL tidak boleh kosong', 'error');
    try {
      // TODO: Verify RLS policy is enabled on this table
      if (editing) { const { error } = await supabase.from('articles').update(form).eq('id', editing.id); if (error) throw error; toast('Artikel diperbarui!', 'success'); }
      else { const { error } = await supabase.from('articles').insert([form]); if (error) throw error; toast('Artikel disimpan!', 'success'); }
      setModal(false); fetchData();
    } catch (e) { toast('Gagal: ' + e.message, 'error'); }
  };

  // TODO: Verify RLS policy is enabled on this table
  const remove = async (id) => { await supabase.from('articles').delete().eq('id', id); toast('Artikel dihapus', 'success'); fetchData(); };
  const generateSlug = (title) => title.toLowerCase().replace(/[^\w\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-').trim();

  const generateWithAI = async () => {
    if (!form.title) return toast('Masukkan judul terlebih dahulu', 'error');
    setAiLoading(true);
    try {
      const prompt = `Kamu adalah penulis konten profesional untuk agensi digital marketing "Aptana" di Indonesia.\n\nJUDUL: "${form.title}"\n\nTulis artikel SEO-optimized dalam Bahasa Indonesia. Output HANYA JSON berikut tanpa teks tambahan:\n{\n  "focus_keyword": "keyword utama",\n  "meta_title": "max 60 karakter",\n  "meta_description": "max 160 karakter",\n  "category": "kategori artikel",\n  "content": "<h2>...</h2><p>...</p>..."\n}`;
      const apiKey = process.env.REACT_APP_OPENROUTER_API_KEY;
      if (!apiKey) throw new Error('API key tidak dikonfigurasi — hubungi admin');
      const res = await fetch('https://openrouter.ai/api/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`,
          'HTTP-Referer': window.location.origin,
        },
        body: JSON.stringify({ model: 'openrouter/auto', messages: [{ role: 'user', content: prompt }], temperature: 0.7, max_tokens: 4000 }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error?.message || `OpenRouter error ${res.status}`);
      const raw = data.choices?.[0]?.message?.content || '';
      if (!raw) throw new Error('AI tidak menghasilkan konten — coba lagi');
      const block = raw.match(/```(?:json)?\s*([\s\S]*?)\s*```/);
      let result = {};
      try { result = JSON.parse(block ? block[1] : raw.match(/\{[\s\S]*\}/)?.[0] || '{}'); } catch (_) { }
      setForm(prev => ({
        ...prev,
        focus_keyword: result.focus_keyword || prev.title.toLowerCase(),
        meta_title: result.meta_title || prev.title,
        meta_description: result.meta_description || '',
        category: result.category || 'Umum',
        content: result.content || raw,
        slug: generateSlug(result.meta_title || prev.title),
      }));
      toast('✨ Artikel berhasil dibuat oleh AI!', 'success');
    } catch (e) { toast('Gagal generate AI: ' + e.message, 'error'); }
    setAiLoading(false);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) return toast('Hanya file gambar', 'error');
    if (file.size > 500 * 1024) return toast('Ukuran maksimal 500KB', 'error');
    setUploadingImage(true);
    try {
      const ext = file.name.split('.').pop();
      const path = `articles/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
      const { error } = await supabase.storage.from('Aptana-Website').upload(path, file, { contentType: file.type });
      if (error) throw error;
      const { data: { publicUrl } } = supabase.storage.from('Aptana-Website').getPublicUrl(path);
      setForm(prev => ({ ...prev, featured_image: publicUrl, content: prev.content + `\n\n<img src="${publicUrl}" alt="${prev.title}" style="max-width:100%;height:auto;border-radius:8px;margin:20px 0;" />\n` }));
      toast('✅ Gambar berhasil diupload!', 'success');
    } catch (e) { toast('Gagal upload: ' + e.message, 'error'); }
    setUploadingImage(false);
    e.target.value = '';
  };

  const filtered = articles.filter(a => a.title?.toLowerCase().includes(search.toLowerCase()));

  return (
    <div>
      <div className="card">
        <div className="card-header">
          <div className="card-title">Manajemen Artikel & Blog</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <div className="search-wrap"><Search size={13} /><input className="search-input" placeholder="Cari artikel..." value={search} onChange={e => setSearch(e.target.value)} /></div>
            <button className="btn btn-ghost" onClick={openNewPopup}><Plus size={13} /> 📢 Newsletter</button>
            <button className="btn btn-primary" onClick={openNew}><Plus size={13} /> Artikel Baru</button>
          </div>
        </div>
        {loading ? <div style={{ textAlign: 'center', padding: 40, color: 'var(--muted)' }}>Memuat...</div>
          : filtered.length === 0 ? <div className="empty"><div className="empty-icon">📝</div><div className="empty-text">Belum ada artikel.</div></div>
            : (
              <table className="table">
                <thead><tr><th>Judul</th><th>Tipe</th><th>Kategori</th><th>Status</th><th>Tanggal</th><th>Aksi</th></tr></thead>
                <tbody>
                  {filtered.map(a => (
                    <tr key={a.id}>
                      <td style={{ fontWeight: 600 }}>{a.title}</td>
                      <td>{a.is_popup ? <span className="badge badge-warn">📢 Popup</span> : <span className="badge badge-blue">📝 Artikel</span>}</td>
                      <td><span className="badge badge-gray">{a.category || 'Umum'}</span></td>
                      <td><span className={`badge ${a.status === 'published' ? 'badge-green' : 'badge-gray'}`}>{a.status === 'published' ? 'Dipublikasikan' : 'Draft'}</span></td>
                      <td style={{ color: 'var(--muted)', fontSize: 11 }}>{new Date(a.created_at).toLocaleDateString('id-ID')}</td>
                      <td>
                        <div className="table-actions">
                          <a href={`${SITE_URL}/blog/${a.slug}`} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}><button className="btn btn-ghost btn-sm"><Eye size={11} /> Buka</button></a>
                          <button className="btn btn-ghost btn-sm" onClick={() => openEdit(a)}><Edit2 size={11} /> Edit</button>
                          <button className="btn btn-danger btn-sm" onClick={() => remove(a.id)}><Trash2 size={11} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
        }
      </div>

      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(false)}>
          <div className="modal" style={{ maxWidth: 760 }}>
            <div className="modal-header"><div className="modal-title">{editing ? 'Edit' : (form.is_popup ? '📢 Newsletter / Popup Baru' : 'Artikel Baru')}</div><button className="btn btn-ghost btn-sm" onClick={() => setModal(false)}><X size={13} /></button></div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Judul</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input className="form-input" style={{ flex: 1 }} value={form.title} onChange={e => setForm({ ...form, title: e.target.value, slug: generateSlug(e.target.value) })} placeholder="Judul artikel..." />
                  <button className="btn btn-yellow" onClick={generateWithAI} disabled={aiLoading || !form.title}>{aiLoading ? '🤖 Menulis...' : '✨ Generate AI'}</button>
                </div>
              </div>
              <div className="form-group"><label className="form-label">Slug URL</label><input className="form-input" value={form.slug} onChange={e => setForm({ ...form, slug: e.target.value })} placeholder="url-artikel" /></div>
              <div className="form-group"><label className="form-label">Focus Keyword</label><input className="form-input" value={form.focus_keyword} onChange={e => setForm({ ...form, focus_keyword: e.target.value })} placeholder="keyword utama" /></div>
              <div className="form-group">
                <label className="form-label">Meta Title</label>
                <input className="form-input" value={form.meta_title} onChange={e => setForm({ ...form, meta_title: e.target.value })} placeholder="Max 60 karakter" />
                <div style={{ fontSize: 11, color: form.meta_title.length > 60 ? 'var(--danger)' : 'var(--muted)', marginTop: 2 }}>{form.meta_title.length}/60</div>
              </div>
              <div className="form-group">
                <label className="form-label">Meta Description</label>
                <textarea className="form-input" style={{ minHeight: 60 }} value={form.meta_description} onChange={e => setForm({ ...form, meta_description: e.target.value })} placeholder="Max 160 karakter" />
                <div style={{ fontSize: 11, color: form.meta_description.length > 160 ? 'var(--danger)' : 'var(--muted)', marginTop: 2 }}>{form.meta_description.length}/160</div>
              </div>
              <div className="form-group"><label className="form-label">Kategori</label><input className="form-input" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} placeholder="Berita, Tips, Produk..." /></div>
              <div className="form-group">
                <label className="form-label">Upload Gambar (maks 500KB)</label>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <label style={{ padding: '7px 14px', background: 'var(--surface2)', border: '1px dashed var(--border)', borderRadius: 6, cursor: 'pointer', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                    📷 Pilih Gambar
                    <input type="file" accept="image/*" onChange={handleImageUpload} style={{ display: 'none' }} disabled={uploadingImage} />
                  </label>
                  {uploadingImage && <span style={{ fontSize: 12, color: 'var(--muted)' }}>Mengupload...</span>}
                  {form.featured_image && <span style={{ fontSize: 12, color: 'var(--success)' }}>✅ Gambar tersimpan</span>}
                </div>
              </div>
              {form.featured_image && <img src={form.featured_image} alt="Preview" style={{ maxWidth: '100%', maxHeight: 140, borderRadius: 8, marginBottom: 12, border: '1px solid var(--border)' }} />}
              <div className="form-group">
                <label className="form-label">Isi Artikel</label>
                <div style={{ display: 'flex', gap: 4, padding: '6px 10px', background: 'var(--surface2)', borderRadius: '7px 7px 0 0', border: '1px solid var(--border)', borderBottom: 'none' }}>
                  {[['B', 'bold', { fontWeight: 700 }], ['I', 'italic', { fontStyle: 'italic' }], ['U', 'underline', { textDecoration: 'underline' }]].map(([label, cmd, style]) => (
                    <button key={cmd} type="button" onClick={() => document.execCommand(cmd)} style={{ padding: '4px 10px', borderRadius: 4, border: '1px solid var(--border)', background: 'white', fontSize: 12, cursor: 'pointer', ...style }}>{label}</button>
                  ))}
                  {[['H2', 'h2'], ['H3', 'h3']].map(([label, tag]) => (
                    <button key={tag} type="button" onClick={() => document.execCommand('formatBlock', false, tag)} style={{ padding: '4px 10px', borderRadius: 4, border: '1px solid var(--border)', background: 'white', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>{label}</button>
                  ))}
                  <button type="button" onClick={() => document.execCommand('insertUnorderedList')} style={{ padding: '4px 10px', borderRadius: 4, border: '1px solid var(--border)', background: 'white', fontSize: 12, cursor: 'pointer' }}>• List</button>
                </div>
                <div
                  contentEditable
                  dangerouslySetInnerHTML={{ __html: form.content }}
                  onInput={e => setForm({ ...form, content: e.currentTarget.innerHTML })}
                  style={{ minHeight: 280, maxHeight: 400, overflow: 'auto', padding: 16, background: 'white', border: '1px solid var(--border)', borderRadius: '0 0 7px 7px', fontSize: 14, lineHeight: 1.8, outline: 'none' }}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Status</label>
                <select className="form-input form-select" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                  <option value="draft">Draft</option>
                  <option value="published">Dipublikasikan</option>
                </select>
              </div>

              {/* Popup toggle */}
              <div className="form-group" style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 8, padding: '14px 16px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.is_popup} onChange={e => setForm({ ...form, is_popup: e.target.checked, popup_pages: e.target.checked ? form.popup_pages : [] })} />
                  <span style={{ fontSize: 13, fontWeight: 600 }}>📢 Tampilkan sebagai Popup di halaman website</span>
                </label>
                {form.is_popup && (
                  <div style={{ marginTop: 12 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 8 }}>Pilih halaman yang menampilkan popup ini</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      {POPUP_PAGES.map(({ slug, label }) => (
                        <label key={slug} style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', fontSize: 13 }}>
                          <input type="checkbox" checked={(form.popup_pages || []).includes(slug)} onChange={() => togglePopupPage(slug)} />
                          {label}
                        </label>
                      ))}
                    </div>
                    <div style={{ marginTop: 10, fontSize: 11, color: 'var(--muted)' }}>
                      💡 Klik gambar popup → diarahkan ke halaman artikel lengkap (/blog/slug)
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setModal(false)}>Batal</button>
              <button className="btn btn-primary" onClick={save}><Save size={13} /> Simpan</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// USER MANAGEMENT
// ⚠️ createUser / deleteUser calls require service-role access.
// Move these to a server-side Edge Function. The UI below is kept
// intact but the admin calls will need a backend endpoint.
// ============================================================
function UserManagementPage({ toast }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ email: '', name: '', password: '', is_super_admin: false, features: { dashboard: true, leads: false, traffic: false, articles: false, designer: false } });

  const ALL_FEATURES = { dashboard: true, leads: true, traffic: true, articles: true, designer: true, users: true };

  const fetchUsers = async () => { setLoading(true); const { data } = await supabase.from('users').select('*').order('created_at', { ascending: false }); setUsers(data || []); setLoading(false); };
  useEffect(() => { fetchUsers(); }, []);

  const openNew = () => { setEditing(null); setForm({ email: '', name: '', password: '', is_super_admin: false, features: { dashboard: true, leads: false, traffic: false, articles: false, designer: false } }); setModal(true); };
  const openEdit = (user) => { setEditing(user); setForm({ email: user.email, name: user.name, is_super_admin: user.is_super_admin || false, features: user.is_super_admin ? ALL_FEATURES : (user.features || {}) }); setModal(true); };

  const save = async () => {
    if (!form.email || !form.name) return toast('Email dan nama harus diisi', 'error');
    const features = form.is_super_admin ? ALL_FEATURES : form.features;
    if (editing) {
      // TODO: Verify RLS policy is enabled on this table
      const { error } = await supabase.from('users').update({ name: form.name, email: form.email, is_super_admin: form.is_super_admin, features }).eq('id', editing.id);
      if (error) return toast('Gagal update: ' + error.message, 'error');
      toast('User diperbarui!', 'success');
    } else {
      if (!form.password) return toast('Password harus diisi', 'error');
      if (!supabaseAdmin) return toast('REACT_APP_SUPABASE_SERVICE_ROLE_KEY belum dikonfigurasi', 'error');
      try {
        const { data: authData, error: authErr } = await supabaseAdmin.auth.admin.createUser({
          email: form.email, password: form.password, email_confirm: true,
        });
        if (authErr) return toast('Gagal: ' + authErr.message, 'error');
        const { error: dbErr } = await supabaseAdmin.from('users').insert([{
          email: form.email, name: form.name,
          is_super_admin: !!form.is_super_admin, features,
          auth_uid: authData.user.id,
        }]);
        if (dbErr) {
          await supabaseAdmin.auth.admin.deleteUser(authData.user.id);
          return toast('Gagal: ' + dbErr.message, 'error');
        }
        toast('User berhasil dibuat! ✅', 'success');
      } catch (e) {
        return toast('Gagal: ' + e.message, 'error');
      }
    }
    setModal(false); fetchUsers();
  };

  const remove = async (user) => {
    if (!window.confirm(`Hapus user ${user.email}?`)) return;
    if (!supabaseAdmin) return toast('REACT_APP_SUPABASE_SERVICE_ROLE_KEY belum dikonfigurasi', 'error');
    try {
      if (user.auth_uid) {
        const { error: authErr } = await supabaseAdmin.auth.admin.deleteUser(user.auth_uid);
        if (authErr) return toast('Gagal hapus: ' + authErr.message, 'error');
      }
      const { error: dbErr } = await supabaseAdmin.from('users').delete().eq('id', user.id);
      if (dbErr) return toast('Gagal hapus: ' + dbErr.message, 'error');
      toast('User dihapus ✅', 'success');
      fetchUsers();
    } catch (e) {
      toast('Gagal hapus: ' + e.message, 'error');
    }
  };

  const toggleFeature = (f) => setForm(prev => ({ ...prev, features: { ...prev.features, [f]: !prev.features[f] } }));

  return (
    <div>
      <div className="card">
        <div className="card-header"><div className="card-title">User Management</div><button className="btn btn-primary" onClick={openNew}><Plus size={13} /> Add User</button></div>
        {loading ? <div style={{ textAlign: 'center', padding: 40, color: 'var(--muted)' }}>Memuat...</div> : (
          <table className="table">
            <thead><tr><th>Nama</th><th>Email</th><th>Role</th><th>Fitur</th><th>Tanggal</th><th>Aksi</th></tr></thead>
            <tbody>
              {users.length === 0
                ? <tr><td colSpan="6" style={{ textAlign: 'center', padding: 40, color: 'var(--muted)' }}>Belum ada user</td></tr>
                : users.map(u => (
                  <tr key={u.id}>
                    <td style={{ fontWeight: 600 }}>{u.name}</td>
                    <td style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>{u.email}</td>
                    <td><span className={`badge ${u.is_super_admin ? 'badge-blue' : 'badge-gray'}`}>{u.is_super_admin ? '👑 Super Admin' : 'User'}</span></td>
                    <td><div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>{Object.entries(u.features || {}).filter(([, v]) => v).map(([k]) => <span key={k} style={{ fontSize: 10, padding: '2px 5px', borderRadius: 3, background: 'rgba(30,58,138,0.08)', color: 'var(--accent)' }}>{k}</span>)}</div></td>
                    <td style={{ fontSize: 11, color: 'var(--muted)' }}>{new Date(u.created_at).toLocaleDateString('id-ID')}</td>
                    <td><div className="table-actions"><button className="btn btn-ghost btn-sm" onClick={() => openEdit(u)}><Edit2 size={11} /> Edit</button><button className="btn btn-danger btn-sm" onClick={() => remove(u)}><Trash2 size={11} /></button></div></td>
                  </tr>
                ))
              }
            </tbody>
          </table>
        )}
      </div>

      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(false)}>
          <div className="modal">
            <div className="modal-header"><div className="modal-title">{editing ? 'Edit User' : 'Tambah User Baru'}</div><button className="btn btn-ghost btn-sm" onClick={() => setModal(false)}><X size={13} /></button></div>
            <div className="modal-body">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group"><label className="form-label">Nama</label><input className="form-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Nama lengkap" /></div>
                <div className="form-group"><label className="form-label">Email</label><input className="form-input" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="email@contoh.com" /></div>
              </div>
              {!editing && <div className="form-group"><label className="form-label">Password</label><input className="form-input" type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="Password" /></div>}
              <div className="form-group"><label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}><input type="checkbox" checked={form.is_super_admin} onChange={e => setForm({ ...form, is_super_admin: e.target.checked })} /><span style={{ fontSize: 13, fontWeight: 600 }}>👑 Super Admin (akses penuh)</span></label></div>
              {!form.is_super_admin && (
                <div className="form-group">
                  <label className="form-label">Pilih Fitur</label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    {[['dashboard', '📊 Dashboard'], ['leads', '👥 Leads'], ['traffic', '📈 Traffic'], ['articles', '📝 Artikel'], ['designer', '🎨 Website Design']].map(([key, label]) => (
                      <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', fontSize: 13 }}>
                        <input type="checkbox" checked={form.features[key] || false} onChange={() => toggleFeature(key)} />
                        {label}
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer"><button className="btn btn-ghost" onClick={() => setModal(false)}>Batal</button><button className="btn btn-primary" onClick={save}><Save size={13} /> {editing ? 'Update' : 'Tambah'}</button></div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// DASHBOARD
// ============================================================
function AdminPasswordPanel({ toast }) {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);

  const savePassword = async () => {
    if (password.length < 8) return toast('Password minimal 8 karakter.', 'error');
    if (password !== confirmPassword) return toast('Konfirmasi password tidak cocok.', 'error');

    setSaving(true);
    const { error } = await supabase.auth.updateUser({ password });
    setSaving(false);
    if (error) return toast('Gagal mengganti password: ' + error.message, 'error');

    setPassword('');
    setConfirmPassword('');
    toast('Password akun berhasil diganti.', 'success');
  };

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div className="card-header"><div className="card-title">Ganti Password Akun</div></div>
      <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 14 }}>Perubahan hanya berlaku untuk akun SuperAdmin yang sedang login.</p>
      <div style={{ maxWidth: 420 }}>
        <div className="form-group">
          <label className="form-label">Password baru</label>
          <input className="form-input" type="password" autoComplete="new-password" minLength={8} value={password} onChange={e => setPassword(e.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label">Konfirmasi password baru</label>
          <input className="form-input" type="password" autoComplete="new-password" minLength={8} value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} />
        </div>
        <button className="btn btn-primary" onClick={savePassword} disabled={saving || !password || !confirmPassword}>
          {saving ? 'Menyimpan...' : 'Simpan Password'}
        </button>
      </div>
    </div>
  );
}

function Dashboard({ stats, session, toast }) {
  return (
    <div>
      <div className="stats-grid">
        <div className="stat-card"><div className="stat-label">Total Artikel</div><div className="stat-value" style={{ color: 'var(--accent)' }}>{stats.articles}</div><div className="stat-change">📝 Tersimpan di CMS</div></div>
        <div className="stat-card"><div className="stat-label">Halaman Website</div><div className="stat-value" style={{ color: 'var(--success)' }}>{stats.pages}</div><div className="stat-change">🌐 Dikelola via CMS</div></div>
        <div className="stat-card"><div className="stat-label">Total User</div><div className="stat-value" style={{ color: 'var(--warn)' }}>{stats.users}</div><div className="stat-change">👥 Terdaftar di CMS</div></div>
        <div className="stat-card"><div className="stat-label">Status Website</div><div className="stat-value" style={{ color: 'var(--success)', fontSize: 18, paddingTop: 6 }}>🟢 Online</div><div className="stat-change">aptana.co.id</div></div>
      </div>
      <div className="card">
        <div className="card-header"><div className="card-title">Selamat Datang di Aptana Marketing CMS 👋</div></div>
        <p style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.9 }}>Pusat kendali marketing digital Aptana. Gunakan menu di kiri untuk mengelola seluruh aset digital perusahaan.</p>
      </div>
      {session?.user?.email?.toLowerCase() === ADMIN_EMAIL.toLowerCase() && <AdminPasswordPanel toast={toast} />}
    </div>
  );
}

// ============================================================
// SETTINGS

// ============================================================
// MAIN APP
// ============================================================
export default function App() {
  const [session, setSession] = useState(null);
  const [page, setPage] = useState('dashboard');
  const [toast, setToast] = useState(null);
  const [stats, setStats] = useState({ articles: 0, pages: 0, users: 0 });
  const [userFeatures, setUserFeatures] = useState(null);
  const [isWebsiteMode, setIsWebsiteMode] = useState(false);
  const [currentPath, setCurrentPath] = useState('home');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [showBell, setShowBell] = useState(false);
  const bellRef = useRef(null);

  useEffect(() => {
    const path = window.location.pathname;
    const isCMS = path.startsWith('/cms') || path.startsWith('/admin');
    if (!isCMS) {
      setIsWebsiteMode(true);
      setCurrentPath(path === '/' ? 'home' : path.replace(/^\//, ''));
    }
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session) loadUserFeatures(session.user.email, session.user.id);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, s) => {
      setSession(s);
      if (s) loadUserFeatures(s.user.email, s.user.id);
    });
    return () => subscription.unsubscribe();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadUserFeatures = async (email, authUid) => {
    if (email === ADMIN_EMAIL) {
      setUserFeatures({ dashboard: true, leads: true, traffic: true, articles: true, designer: true, users: true });
      return;
    }
    const client = supabaseAdmin || supabase;
    let data = null;
    if (authUid) {
      const res = await client.from('users').select('is_super_admin, features').eq('auth_uid', authUid).single();
      data = res.data;
    }
    if (!data) {
      const res = await client.from('users').select('is_super_admin, features').ilike('email', email).single();
      data = res.data;
    }
    if (data?.is_super_admin) setUserFeatures({ dashboard: true, leads: true, traffic: true, articles: true, designer: true, users: true });
    else setUserFeatures(data?.features || { dashboard: true });
  };

  const hasAccess = (feature) => !!(userFeatures?.[feature]);

  useEffect(() => {
    if (!session) return;
    const loadStats = async () => {
      const [a, p, u] = await Promise.all([
        supabase.from('articles').select('id', { count: 'exact', head: true }),
        supabase.from('pages').select('id', { count: 'exact', head: true }),
        supabase.from('users').select('id', { count: 'exact', head: true }),
      ]);
      setStats({ articles: a.count || 0, pages: p.count || 0, users: u.count || 0 });
    };
    loadStats();
  }, [session, page]);

  // ── Notification polling (every 30s) ────────────────────────
  useEffect(() => {
    if (!session) return;
    const fetchNotifs = async () => {
      const { data } = await supabase
        .from('notifications')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(30);
      if (data) setNotifications(data);
    };
    fetchNotifs();
    const interval = setInterval(fetchNotifs, 30000);
    return () => clearInterval(interval);
  }, [session]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close bell panel on outside click
  useEffect(() => {
    if (!showBell) return;
    const handler = (e) => {
      if (bellRef.current && !bellRef.current.contains(e.target)) setShowBell(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showBell]);

  const markAllRead = async () => {
    const unreadIds = notifications.filter(n => !n.read).map(n => n.id);
    if (!unreadIds.length) return;
    // TODO: Verify RLS policy is enabled on this table
    await supabase.from('notifications').update({ read: true }).in('id', unreadIds);
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  const handleBellOpen = () => {
    setShowBell(v => !v);
    if (!showBell) markAllRead();
  };

  const timeAgo = (ts) => {
    const diff = Math.floor((Date.now() - new Date(ts)) / 1000);
    if (diff < 60) return `${diff}d lalu`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m lalu`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}j lalu`;
    return `${Math.floor(diff / 86400)}h lalu`;
  };

  const SHEET_LABEL = { aptana: '📋 Aptana Leads', kuponku: '🎫 Kuponku Leads', 'salesql-kuponku': '🔍 SalesQL Kuponku', 'salesql-multichannel': '📡 SalesQL Multichannel', salesql: '🔍 SalesQL Leads' };

  const showToast = (message, type = 'success') => setToast({ message, type });

  if (isWebsiteMode) return <WebsitePreview currentPage={currentPath} />;
  if (!session) return <LoginPage />;

  const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'leads', label: 'Leads', icon: Users },
    { id: 'traffic', label: 'Traffic Website', icon: TrendingUp },
    { id: 'articles', label: 'Artikel & Blog', icon: FileText },
    { id: 'designer', label: 'Website Design', icon: Palette },
    { id: 'users', label: 'User Management', icon: Settings },
  ].filter(item => hasAccess(item.id));

  const PAGE_TITLE = { dashboard: 'Dashboard', leads: 'Leads', traffic: 'Traffic Website', articles: 'Artikel & Blog', designer: 'Website Design', users: 'User Management' };

  return (
    <div className="cms-layout">
      <CMSSidebar navItems={navItems} page={page} setPage={setPage} session={session} collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed} />

      <div className="main">
        <div className="topbar">
          <div className="topbar-title">{PAGE_TITLE[page]}</div>
          <div className="notif-wrap" ref={bellRef}>
            <button className="notif-btn" onClick={handleBellOpen} title="Notifikasi">
              <Bell size={16} />
              {notifications.filter(n => !n.read).length > 0 && (
                <span className="notif-badge">
                  {notifications.filter(n => !n.read).length > 9 ? '9+' : notifications.filter(n => !n.read).length}
                </span>
              )}
            </button>
            {showBell && (
              <div className="notif-panel">
                <div className="notif-head">
                  <span className="notif-head-title">Notifikasi Leads</span>
                  {notifications.some(n => !n.read) && (
                    <button className="notif-mark-read" onClick={markAllRead}>Tandai semua dibaca</button>
                  )}
                </div>
                <div className="notif-list">
                  {notifications.length === 0 ? (
                    <div className="notif-empty">
                      <div style={{ fontSize: 28, marginBottom: 8 }}>🔔</div>
                      Belum ada notifikasi
                    </div>
                  ) : notifications.map(n => (
                    <div key={n.id} className={`notif-item${n.read ? '' : ' unread'}`}>
                      <div className={`notif-dot${n.read ? ' read' : ''}`} />
                      <div className="notif-body">
                        <div className="notif-title">{SHEET_LABEL[n.sheet] || n.sheet} diperbarui</div>
                        <div className="notif-sub">
                          {n.sheet_tab && `Tab: ${n.sheet_tab} · `}
                          {n.cell_range && `Sel: ${n.cell_range}`}
                          {n.editor_email && ` · ${n.editor_email}`}
                        </div>
                        {(n.old_value || n.new_value) && (
                          <div className="notif-sub" style={{ marginTop: 2 }}>
                            {n.old_value && <span style={{ color: '#dc2626' }}>"{n.old_value}" </span>}
                            {n.old_value && n.new_value && '→ '}
                            {n.new_value && <span style={{ color: '#16a34a' }}>"{n.new_value}"</span>}
                          </div>
                        )}
                        <div className="notif-time">{timeAgo(n.created_at)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="content">
          {page === 'dashboard' && hasAccess('dashboard') && <Dashboard stats={stats} session={session} toast={showToast} />}
          {page === 'leads' && hasAccess('leads') && <LeadsPage />}
          {page === 'traffic' && hasAccess('traffic') && <TrafficPage />}
          {page === 'articles' && hasAccess('articles') && <Articles toast={showToast} />}
          {page === 'designer' && hasAccess('designer') && <WebsiteDesigner toast={showToast} />}
          {page === 'users' && hasAccess('users') && <UserManagementPage toast={showToast} />}
          {!hasAccess(page) && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 400, textAlign: 'center' }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
              <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>Akses Ditolak</div>
              <div style={{ fontSize: 14, color: 'var(--muted)' }}>Kamu tidak punya akses ke fitur ini.</div>
              <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 8 }}>Hubungi admin: {ADMIN_EMAIL}</div>
            </div>
          )}
        </div>
      </div>

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}
